import { faArrowRotateRight } from '@fortawesome/pro-light-svg-icons/faArrowRotateRight';
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';
import { faPencil } from '@fortawesome/pro-light-svg-icons/faPencil';
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons/faTrashAlt';
import { faCirclePlus, faPen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { status as AppointmentStatusesIds } from '@spike/appointment-model';
import { changeStatusAppointmentThunk } from '@spike/bookings-action';
import { AppointmentOrderItem } from '@spike/order-model';
import { reloadOrderThunk } from '@spike/orders-action';
import { getCareReportByAppointmentIdThunk } from '@spike/reports-action';
import clsx from 'clsx';
import AppointmentData from 'components/Checkout2/UI/AppointmentData';
import { AddEditEvaluationReport } from '../AddEditEvaluationReport/AddEditEvaluationReport';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import { useApiClientWrapper, useHasPremiumAccess, useTimeZone } from 'hooks';
import { FunctionComponent, useEffect, useState } from 'react';
import { dispatch } from 'store';
import StatusButton from '../StatusButton';
import ToolbarButton, { ToolbarOption } from '../UI/ToolbarButton';
import Pet from '@spike/pet-model';
import { EvaluationResult } from './EvaluationResult/EvaluationResult';
import styles from './AppointmentItem.module.scss';
import { formatDateTime } from 'utils/DateUtils';
import BoardingInfo from './BoardingInfo';

export interface AppointmentItemProps {
    appointmentItem: AppointmentOrderItem;
    editable: boolean;
    editingOrder: boolean;
    savingOther?: boolean;
    withoutOption?: boolean;
    /** Parent: all order appointments closed and !reopenClosedBookings — hide Edit Service in toolbar. */
    hideEditWhenOrderFullyClosed?: boolean;
    onEdit?: () => void;
    onRebook?: () => void;
    onAddReport?: (appointmentId: number, businessAreaId: string, serviceId?: number, pet?: Pet) => void;
    onViewReport?: (
        appointmentId: number,
        businessAreaId: string,
        serviceId?: number,
        pet?: Pet,
        evaluationReportId?: number
    ) => void;
    onDelete?: () => void;
    onStartSavingStatus?: () => void;
    onStopSavingStatus?: () => void;
    resultsDrawerZIndex?: number;
    className?: string;
}

const disableEditionStatuses = [
    AppointmentStatusesIds.CANCELLED,
    AppointmentStatusesIds.NO_SHOW,
    AppointmentStatusesIds.DECLINED
];

const canceledStatuses = [AppointmentStatusesIds.CANCELLED, AppointmentStatusesIds.NO_SHOW];

const HERE = 3;
const PICKUP = 5;

export const AppointmentItem: FunctionComponent<AppointmentItemProps> = props => {
    const theme = useTheme();
    const timezone = useTimeZone();
    const apiClientWrapper = useApiClientWrapper();

    const serviceColor = props.appointmentItem.appointment.services[0].color;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const hasReportsPremiumAccess = useHasPremiumAccess('groomming_reports');

    const [isSavingStatus, setIsSavingStatus] = useState(false);
    const [pendingEvaluationStatusId, setPendingEvaluationStatusId] = useState<number>();

    const disableEditionByStatus = disableEditionStatuses.includes(props.appointmentItem.appointment.status.id);

    const savedStandardReportExists = Boolean(
        props.appointmentItem.appointment.reports.dentalReportId ||
            props.appointmentItem.appointment.reports.groomingReportId
    );

    const appointmentId = props.appointmentItem.appointment.id!;
    const businessAreaId = props.appointmentItem.appointment.services[0].businessArea.id;
    const serviceId = props.appointmentItem.appointment.services[0].id;
    const evaluationReportId = props.appointmentItem.appointment.reports.evaluationReportId;
    const isCareReport = businessAreaId === 'daycare' || businessAreaId === 'boarding';
    const isStandardReport = businessAreaId === 'dental' || businessAreaId === 'grooming';
    const isEvaluationReport = businessAreaId === 'evaluation';
    const reportSupported = isStandardReport || isCareReport || isEvaluationReport;
    const [savedCareReportExists, setSavedCareReportExists] = useState<boolean | undefined>(
        isCareReport ? undefined : false
    );

    useEffect(() => {
        if (!isCareReport) {
            setSavedCareReportExists(false);
            return;
        }

        let active = true;
        setSavedCareReportExists(undefined);
        dispatch(getCareReportByAppointmentIdThunk(apiClientWrapper, appointmentId)).then(report => {
            if (active) setSavedCareReportExists(Boolean(report));
        });

        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appointmentId, isCareReport]);

    const reportExists = isCareReport
        ? savedCareReportExists === true
        : isEvaluationReport
        ? evaluationReportId != null
        : savedStandardReportExists;
    const reportExistenceResolved = !isCareReport || savedCareReportExists !== undefined;

    const isCanceled = canceledStatuses.includes(props.appointmentItem.appointment.status.id);
    const reportActionLabel = isEvaluationReport
        ? reportExists
            ? 'Edit Results'
            : 'Add Results'
        : reportExists
        ? 'Edit Report'
        : 'Add Report';

    const hasReportAccess = hasReportsPremiumAccess || isCareReport || isEvaluationReport;
    const reportCallbackAvailable = isCareReport
        ? Boolean(props.onAddReport)
        : reportExists
        ? Boolean(props.onViewReport)
        : Boolean(props.onAddReport);
    const showReportAction =
        !props.editingOrder &&
        !isCanceled &&
        reportSupported &&
        reportExistenceResolved &&
        hasReportAccess &&
        reportCallbackAvailable;

    const reportActionHandler = () => {
        if ((isStandardReport || isEvaluationReport) && reportExists) {
            props.onViewReport?.(
                appointmentId,
                businessAreaId,
                serviceId,
                props.appointmentItem.pet,
                evaluationReportId
            );
            return;
        }

        props.onAddReport?.(appointmentId, businessAreaId, serviceId, props.appointmentItem.pet);
    };

    const hasEndDate = props.appointmentItem.appointment?.status?.id === PICKUP;
    const hasStartDate = props.appointmentItem.appointment?.status?.id === HERE || hasEndDate;

    const toolbarOptions: Array<ToolbarOption> = [
        {
            id: 'edit',
            icon: faPencil,
            label: 'Edit Service',
            onClick: () => props.onEdit && props.onEdit()
        },
        {
            id: 'rebook',
            icon: faArrowRotateRight,
            label: 'Rebook',
            onClick: () => props.onRebook && props.onRebook()
        },
        ...(businessAreaId === 'evaluation'
            ? [
                  {
                      id: 'report',
                      icon: faCopy,
                      label: evaluationReportId != null ? 'Edit Results' : 'Add Results',
                      onClick: () =>
                          evaluationReportId != null
                              ? props.onViewReport?.(
                                    appointmentId,
                                    businessAreaId,
                                    serviceId,
                                    props.appointmentItem.pet,
                                    evaluationReportId
                                )
                              : props.onAddReport?.(appointmentId, businessAreaId, serviceId, props.appointmentItem.pet)
                  }
              ]
            : []),
        {
            id: 'delete',
            icon: faTrashAlt,
            label: 'Delete',
            color: '#EF4F57',
            onClick: () => props.onDelete && props.onDelete()
        }
    ]
        .filter(option => props.editable || (option.id !== 'edit' && option.id !== 'delete'))
        .filter(option => !props.editingOrder || (option.id !== 'rebook' && option.id !== 'report'))
        .filter(option => props.onDelete || option.id !== 'delete')
        .filter(option => !disableEditionByStatus || (option.id !== 'edit' && option.id !== 'delete'))
        .filter(option => !props.hideEditWhenOrderFullyClosed || option.id !== 'edit')
        .filter(option => option.id !== 'report' || Boolean(props.onAddReport || props.onViewReport))
        .filter(option => !isCanceled || option.id !== 'report');

    const toolbarTransportationOptions: Array<ToolbarOption> = [
        {
            id: 'edit',
            icon: faPencil,
            label: 'Edit Service',
            onClick: () => props.onEdit && props.onEdit()
        },
        {
            id: 'delete',
            icon: faTrashAlt,
            label: 'Delete',
            color: '#EF4F57',
            onClick: () => props.onDelete && props.onDelete()
        }
    ]
        .filter(option => props.editable || (option.id !== 'edit' && option.id !== 'delete'))
        .filter(option => !props.editingOrder || (option.id !== 'rebook' && option.id !== 'report'))
        .filter(option => props.onDelete || option.id !== 'delete')
        .filter(option => !disableEditionByStatus || (option.id !== 'edit' && option.id !== 'delete'))
        .filter(option => !props.hideEditWhenOrderFullyClosed || option.id !== 'edit');

    const startSavingStatusHandler = () => {
        props.onStartSavingStatus && props.onStartSavingStatus();
        setIsSavingStatus(true);
    };

    const stopSavingStatusHandler = () => {
        props.onStopSavingStatus && props.onStopSavingStatus();
        setIsSavingStatus(false);
    };

    return (
        <Box>
            <div className={styles.box}>
                <Box
                    className={styles.emptyBox}
                    style={{ borderLeftColor: serviceColor ?? '#ffffff' }}
                />
                <Box
                    className={clsx(styles.container, props.className, {
                        [styles.withoutColor]: !serviceColor
                    })}
                >
                    <Box className={styles.bodyContainer}>
                        <div className={styles.bodyInfo}>
                            <Box className={styles.leftContainer}>
                                <AppointmentData appointmentItem={props.appointmentItem} />
                            </Box>
                            <Box className={styles.rightContainer}>
                                {!isMobile && !props.editingOrder && (
                                    <StatusButton
                                        appointmentId={props.appointmentItem.appointment.id ?? 0}
                                        apppointmentMarketplaceId={props.appointmentItem.appointment.marketplaceId}
                                        inRecurringSeries={props.appointmentItem.appointment.recurringSeriesId != null}
                                        editScopeOrigin="order_detail"
                                        appointmentStatus={props.appointmentItem.appointment.status}
                                        appointmentClosedAt={props.appointmentItem.appointment.closedAt}
                                        appointmentCancelationReasonId={
                                            props.appointmentItem.appointment.cancelationReasonId
                                        }
                                        businessAreaId={businessAreaId}
                                        appointmentTrainingType={
                                            props.appointmentItem.appointment.details?.training?.type
                                        }
                                        appointmentWalkingType={
                                            props.appointmentItem.appointment.details?.walking?.type
                                        }
                                        recurringUuid={props.appointmentItem.appointment.recurringUuid}
                                        customerFirstName={props.appointmentItem.appointment.customer.firstName}
                                        customerLastName={props.appointmentItem.appointment.customer.lastName}
                                        reloadOrderAfterStatusChange={true}
                                        onStartSaving={startSavingStatusHandler}
                                        onStopSaving={stopSavingStatusHandler}
                                        hasEvaluationResult={evaluationReportId != null}
                                        onEvaluationResultRequired={setPendingEvaluationStatusId}
                                        disabled={props.savingOther || props.withoutOption}
                                    />
                                )}
                                {(!props.withoutOption || props.editingOrder) && (
                                    <Box className={styles.toolbarContainer}>
                                        {!isSavingStatus && (
                                            <ToolbarButton
                                                zIndex={props.resultsDrawerZIndex}
                                                options={
                                                    businessAreaId === 'transportation'
                                                        ? toolbarTransportationOptions
                                                        : toolbarOptions
                                                }
                                                disabled={props.savingOther}
                                            />
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </div>
                    </Box>
                    {businessAreaId === 'boarding' && (
                        <div className={styles.boardingInfo}>
                            <BoardingInfo appointmentItem={props.appointmentItem} />
                        </div>
                    )}
                    {isMobile && !props.editingOrder && (
                        <Box className={styles.footerContainer}>
                            <StatusButton
                                appointmentId={props.appointmentItem.appointment.id!}
                                apppointmentMarketplaceId={props.appointmentItem.appointment.marketplaceId}
                                inRecurringSeries={props.appointmentItem.appointment.recurringSeriesId != null}
                                editScopeOrigin="order_detail"
                                businessAreaId={businessAreaId}
                                appointmentTrainingType={props.appointmentItem.appointment.details?.training?.type}
                                appointmentWalkingType={props.appointmentItem.appointment.details?.walking?.type}
                                reloadOrderAfterStatusChange={true}
                                disabled={props.savingOther || props.withoutOption}
                                appointmentStatus={props.appointmentItem.appointment.status}
                                appointmentClosedAt={props.appointmentItem.appointment.closedAt}
                                appointmentCancelationReasonId={props.appointmentItem.appointment.cancelationReasonId}
                                customerFirstName={props.appointmentItem.appointment.customer.firstName}
                                customerLastName={props.appointmentItem.appointment.customer.lastName}
                                hasEvaluationResult={evaluationReportId != null}
                                onEvaluationResultRequired={setPendingEvaluationStatusId}
                            />
                        </Box>
                    )}
                    {(hasStartDate || hasEndDate) && (
                        <div className={styles.datesContainer}>
                            {hasStartDate && (
                                <div className={styles.date}>
                                    <span className={styles.date}>
                                        Arrived:{' '}
                                        {formatDateTime(props.appointmentItem.appointment?.duration?.from, timezone)}
                                    </span>
                                </div>
                            )}
                            {hasEndDate && (
                                <div className={styles.date}>
                                    <span className={styles.date}>
                                        Picked Up:{' '}
                                        {formatDateTime(props.appointmentItem.appointment?.duration?.to, timezone)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {props.appointmentItem.appointment.reports.evaluationReportId !== undefined &&
                        props.appointmentItem.appointment.reports.evaluationReportId !== null && (
                            <div className={styles.resultContainer}>
                                <EvaluationResult
                                    evaluationReportId={props.appointmentItem.appointment.reports.evaluationReportId}
                                    isCanceled={isCanceled}
                                    onEdit={() => {
                                        props.onViewReport &&
                                            props.onViewReport(
                                                appointmentId,
                                                businessAreaId,
                                                serviceId,
                                                props.appointmentItem.pet,
                                                props.appointmentItem.appointment.reports.evaluationReportId
                                            );
                                    }}
                                />
                            </div>
                        )}
                    <div className={styles.appointmentDivider}>
                        <div className={styles.reportActions}>
                            {showReportAction && (
                                <button
                                    type="button"
                                    className={styles.reportAction}
                                    onClick={reportActionHandler}
                                >
                                    <FontAwesomeIcon icon={reportExists ? faPen : faCirclePlus} />
                                    {reportActionLabel}
                                </button>
                            )}
                        </div>
                    </div>
                </Box>
                {pendingEvaluationStatusId != null && (
                    <Drawer
                        zIndex={props.resultsDrawerZIndex}
                        onClose={() => setPendingEvaluationStatusId(undefined)}
                    >
                        <AddEditEvaluationReport
                            appointmentId={appointmentId}
                            serviceId={serviceId}
                            pet={props.appointmentItem.pet}
                            onBack={() => setPendingEvaluationStatusId(undefined)}
                            onClose={() => setPendingEvaluationStatusId(undefined)}
                            onSave={() => {
                                const statusId = pendingEvaluationStatusId;
                                setPendingEvaluationStatusId(undefined);
                                dispatch(changeStatusAppointmentThunk(apiClientWrapper, appointmentId, statusId)).then(
                                    () => dispatch(reloadOrderThunk(apiClientWrapper))
                                );
                            }}
                        />
                    </Drawer>
                )}
            </div>
        </Box>
    );
};

export default AppointmentItem;
